import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { IconPause, IconPlay } from './ui/icons';
import { pseudoPeaks } from './waveform/model';
import { usePreviewPlayer } from '../hooks/usePreviewPlayer';
import type { ListingSummary } from '../lib/types';

function WaveThumb({ seed, playing }: { seed: string; playing: boolean }) {
  const peaks = pseudoPeaks(seed, 48);
  return (
    <div className="flex h-12 items-center gap-px" aria-hidden>
      {peaks.map((p, i) => (
        <span
          key={i}
          className={`w-full ${playing ? 'wave-playing bg-wave-hot' : 'bg-wave-rest'}`}
          style={{
            height: `${Math.max(8, p * 100)}%`,
            ...(playing ? { animationDelay: `${(i % 12) * 55}ms` } : undefined),
          }}
        />
      ))}
    </div>
  );
}

/** Listing card + price. Money renders in the listing's own ISO currency (D-009: USD). */
export function ListingCard({ listing }: { listing: ListingSummary }) {
  const { t, fmt } = useI18n();
  const preview = usePreviewPlayer(listing.id);
  const playing = preview.state === 'playing';
  const a = listing.asset;
  const meta = t.market.detailMeta(
    a.kind.replace('_', ' ').toUpperCase(),
    a.key_camelot ?? '—',
    fmt.bpm(a.bpm) || '—',
  );

  return (
    <Card interactive className="flex flex-col gap-3 p-3">
      <Link to={`/market/${listing.id}`} className="focus-ring rounded-sm">
        <WaveThumb seed={listing.asset_id} playing={playing} />
        <h3 className="type-heading-2 text-primary mt-3 truncate" title={listing.title}>
          {listing.title}
        </h3>
        <p className="type-data text-secondary tnum mt-1">{meta}</p>
      </Link>

      <div className="mt-auto flex items-end justify-between gap-2 pt-2">
        <div className="min-w-0">
          <span className="type-caption text-muted block truncate">{t.listing.license[listing.license_type]}</span>
          <span className="type-caption text-muted tnum">{fmt.duration(a.duration_ms)}</span>
        </div>
        <div className="text-right">
          <span className="type-price text-accent block tnum">{fmt.money(listing.price_cents, listing.price_currency)}</span>
        </div>
      </div>

      <div className="flex gap-2">
        <Link to={`/market/${listing.id}`} className="grow">
          <Button size="sm" block>
            {t.market.buy}
          </Button>
        </Link>
        <Button
          variant="ghost"
          size="sm"
          onClick={preview.toggle}
          disabled={preview.state === 'unavailable'}
          loading={preview.state === 'loading'}
          aria-label={playing ? t.market.pause : t.market.preview}
          aria-pressed={playing}
          title={preview.state === 'unavailable' ? t.market.previewUnavailable : undefined}
        >
          {playing ? <IconPause size={16} /> : <IconPlay size={16} />}
        </Button>
      </div>
    </Card>
  );
}
